import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/* ═══════════════════════════════════════════
   SMS CREDIT WIDGET (shown at top of SMS tab)
   ═══════════════════════════════════════════ */
const CreditWidget = ({ credits, authHeaders, onRefresh }) => {
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [amount, setAmount] = useState(25);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const recharge = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/sms/credits/recharge`, { amount, note }, authHeaders());
      setRechargeOpen(false);
      setNote('');
      onRefresh();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className={`rounded-xl p-5 mb-6 border ${credits.low_balance ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`} data-testid="sms-credit-widget">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-6">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Balance</p>
            <p className={`text-2xl font-bold ${credits.low_balance ? 'text-red-600' : 'text-gray-900'}`}>${credits.balance?.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">SMS Remaining</p>
            <p className="text-2xl font-bold text-gray-900">{credits.sms_remaining?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Sent</p>
            <p className="text-2xl font-bold text-gray-900">{credits.sms_sent_count?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Cost / SMS</p>
            <p className="text-lg font-semibold text-gray-600">${credits.cost_per_sms}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {credits.low_balance && (
            <span className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-semibold">Low Balance</span>
          )}
          <button onClick={() => setRechargeOpen(!rechargeOpen)} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition" data-testid="recharge-btn">
            + Add Credits
          </button>
        </div>
      </div>
      {credits.low_balance && (
        <p className="text-xs text-red-600 mt-2">Your SMS credit balance is low. Add credits to continue sending messages.</p>
      )}

      {rechargeOpen && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border" data-testid="recharge-form">
          <h4 className="font-semibold text-sm mb-3">Add SMS Credits</h4>
          <div className="flex flex-wrap gap-2 mb-3">
            {(credits.refill_options || [5, 10, 25, 50, 100]).map(opt => (
              <button key={opt} onClick={() => setAmount(opt)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${amount === opt ? 'bg-[#5c0701] text-white border-[#5c0701]' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}>
                ${opt.toFixed(2)}
                <span className="text-xs ml-1 opacity-70">({Math.floor(opt / credits.cost_per_sms)} texts)</span>
              </button>
            ))}
          </div>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional, e.g. 'Monthly top-up')"
            className="w-full px-3 py-2 border rounded-lg text-sm mb-3" />
          <button onClick={recharge} disabled={saving} className="bg-green-600 text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="confirm-recharge">
            {saving ? 'Adding...' : `Add $${amount.toFixed(2)} Credits`}
          </button>
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   SMS CONVERSATION VIEW
   ═══════════════════════════════════════════ */
const ConversationView = ({ phone, contactName, authHeaders, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(() => {
    axios.get(`${API_URL}/api/sms/conversations/${encodeURIComponent(phone)}`, authHeaders())
      .then(res => setMessages(res.data.messages || []))
      .catch(() => {});
  }, [phone, authHeaders]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/sms/send`, { to: phone, message: newMsg, contact_name: contactName }, authHeaders());
      setNewMsg('');
      fetchMessages();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="conversation-view">
      <button onClick={onBack} className="text-[#3a87fe] text-sm mb-4 hover:underline flex items-center gap-1" data-testid="back-to-conversations">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        Back to Conversations
      </button>
      <div className="bg-white rounded-lg border p-4 mb-4">
        <h3 className="font-bold text-sm mb-1">{contactName || phone}</h3>
        <p className="text-xs text-gray-500">{phone}</p>
      </div>

      <div className="bg-gray-50 rounded-lg border p-4 mb-4 max-h-[400px] overflow-y-auto space-y-3" data-testid="message-thread">
        {messages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No messages yet</p>
        ) : messages.map((m, idx) => (
          <div key={idx} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
              m.direction === 'outbound' ? 'bg-[#3a87fe] text-white' : 'bg-white border text-gray-800'
            }`}>
              <p className="text-sm">{m.message}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] opacity-60">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
                {m.status === 'failed' && <span className="text-[10px] text-red-300">Failed</span>}
                {m.type && <span className="text-[10px] opacity-50">{m.type}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type your message..."
          className="flex-1 px-4 py-2.5 border rounded-lg text-sm" data-testid="sms-input"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
        <button onClick={sendMessage} disabled={sending || !newMsg.trim()} className="bg-[#5c0701] text-white px-6 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="send-sms-btn">
          {sending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   MAIN SMS DASHBOARD (exported)
   ═══════════════════════════════════════════ */
const SMSDashboard = ({ authHeaders }) => {
  const [credits, setCredits] = useState({});
  const [tab, setTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [archives, setArchives] = useState([]);
  const [optInStats, setOptInStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // New message form
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [newTo, setNewTo] = useState('');
  const [newName, setNewName] = useState('');
  const [newText, setNewText] = useState('');
  const [sending, setSending] = useState(false);

  // Recharge history
  const [history, setHistory] = useState([]);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/sms/credits/balance`, authHeaders()),
      axios.get(`${API_URL}/api/sms/conversations`, authHeaders()),
      axios.get(`${API_URL}/api/sms/opt-in/status`, authHeaders()),
    ]).then(([credRes, convoRes, optRes]) => {
      setCredits(credRes.data);
      setConversations(convoRes.data.conversations || []);
      setOptInStats(optRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchArchives = () => {
    axios.get(`${API_URL}/api/sms/archive`, authHeaders())
      .then(res => setArchives(res.data.archives || []))
      .catch(() => {});
  };

  const fetchHistory = () => {
    axios.get(`${API_URL}/api/sms/credits/history`, authHeaders())
      .then(res => setHistory(res.data.history || []))
      .catch(() => {});
  };

  const archiveConvo = async (phone) => {
    if (!window.confirm(`Archive conversation with ${phone}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/sms/conversations/${encodeURIComponent(phone)}`, authHeaders());
      fetchAll();
    } catch (err) { alert('Failed to archive'); }
  };

  const restoreArchive = async (id) => {
    try {
      await axios.post(`${API_URL}/api/sms/archive/${id}/restore`, {}, authHeaders());
      fetchArchives();
      fetchAll();
    } catch { alert('Failed to restore'); }
  };

  const sendNewMessage = async () => {
    if (!newTo || !newText) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/sms/send`, { to: newTo, message: newText, contact_name: newName }, authHeaders());
      setShowNewMsg(false);
      setNewTo(''); setNewName(''); setNewText('');
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  if (selectedConvo) {
    return (
      <div data-testid="sms-dashboard">
        <CreditWidget credits={credits} authHeaders={authHeaders} onRefresh={fetchAll} />
        <ConversationView phone={selectedConvo.phone} contactName={selectedConvo.name} authHeaders={authHeaders} onBack={() => { setSelectedConvo(null); fetchAll(); }} />
      </div>
    );
  }

  return (
    <div data-testid="sms-dashboard">
      {/* Help Section */}
      <div className="mb-6">
        <button onClick={() => setHelpOpen(!helpOpen)} className="flex items-center gap-2 text-sm text-[#3a87fe] hover:text-[#2968d9] font-medium transition" data-testid="sms-help-toggle">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {helpOpen ? 'Hide Instructions' : 'How do I use this?'}
          <svg className={`w-4 h-4 transition-transform ${helpOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
        </button>
        {helpOpen && (
          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-5 text-sm text-gray-700 leading-relaxed space-y-3">
            <p><strong>SMS Messaging</strong> lets you text your customers directly from the dashboard.</p>
            <p><strong>How credits work:</strong></p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Every text you send costs <strong>$0.015</strong> (about 1.5 cents).</li>
              <li>Your credit balance is shown at the top. When it gets low, you'll see a red warning.</li>
              <li>Click <strong>"+ Add Credits"</strong> to top up. Choose an amount and click confirm.</li>
              <li>For example: $25 gives you about <strong>1,666 texts</strong>.</li>
            </ul>
            <p><strong>How to send a text:</strong></p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Click <strong>"New Message"</strong>.</li>
              <li>Enter the phone number (with area code, like +12035551234).</li>
              <li>Type your message and click <strong>"Send"</strong>.</li>
              <li>The conversation will appear in your inbox.</li>
            </ol>
            <p><strong>Important — Double Opt-In (Legal Requirement):</strong></p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Before sending marketing texts, customers must reply <strong>YES</strong> to confirm they want texts.</li>
              <li>If they text <strong>STOP</strong>, they're automatically unsubscribed.</li>
              <li>This protects you from legal issues (TCPA compliance).</li>
              <li>Check the <strong>"Opt-In Status"</strong> tab to see who's verified.</li>
            </ul>
            <p className="text-xs text-gray-500 mt-2">Note: SMS sending requires proper configuration. Until then, messages are tracked locally but won't deliver.</p>
          </div>
        )}
      </div>

      {/* Credit Widget */}
      <CreditWidget credits={credits} authHeaders={authHeaders} onRefresh={fetchAll} />

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { id: 'conversations', label: 'Conversations' },
          { id: 'optin', label: 'Opt-In Status' },
          { id: 'archives', label: 'Archives' },
          { id: 'history', label: 'Credit History' },
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'archives') fetchArchives(); if (t.id === 'history') fetchHistory(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === t.id ? 'bg-[#5c0701] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`sms-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
        <button onClick={() => setShowNewMsg(!showNewMsg)} className="ml-auto bg-[#3a87fe] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9]" data-testid="new-message-btn">
          {showNewMsg ? 'Cancel' : '+ New Message'}
        </button>
      </div>

      {/* New Message Form */}
      {showNewMsg && (
        <div className="bg-white border border-[#3a87fe] rounded-lg p-5 mb-6" data-testid="new-message-form">
          <h3 className="font-bold text-sm mb-3">Send New Message</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input value={newTo} onChange={e => setNewTo(e.target.value)} placeholder="Phone number (e.g. +12035551234)" className="px-3 py-2 border rounded-lg text-sm" data-testid="new-msg-phone" />
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Contact name (optional)" className="px-3 py-2 border rounded-lg text-sm" data-testid="new-msg-name" />
          </div>
          <textarea value={newText} onChange={e => setNewText(e.target.value)} placeholder="Type your message..." rows="3" className="w-full px-3 py-2 border rounded-lg text-sm resize-none mb-3" data-testid="new-msg-text" />
          <button onClick={sendNewMessage} disabled={sending || !newTo || !newText} className="bg-[#5c0701] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="new-msg-send">
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      )}

      {/* Conversations Tab */}
      {tab === 'conversations' && (
        <div data-testid="conversations-list">
          {loading ? <div className="text-center py-12 text-gray-400">Loading...</div> :
           conversations.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500 border">
              <p className="font-medium mb-2">No conversations yet</p>
              <p className="text-sm">Send your first message using the "New Message" button above.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((c, idx) => (
                <div key={idx} className="bg-white rounded-lg border p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition"
                  onClick={() => setSelectedConvo({ phone: c.phone, name: c.contact_name })} data-testid={`conversation-${idx}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{c.contact_name || 'Unknown'}</span>
                      <span className="text-xs text-gray-400">{c.phone}</span>
                      {c.unread_count > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{c.unread_count}</span>}
                    </div>
                    <p className="text-xs text-gray-500 truncate max-w-md">
                      {c.last_direction === 'outbound' ? 'You: ' : ''}{c.last_message}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-gray-400">{c.message_count} msgs</span>
                    <button onClick={e => { e.stopPropagation(); archiveConvo(c.phone); }} className="text-xs text-gray-400 hover:text-red-500">Archive</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Opt-In Status Tab */}
      {tab === 'optin' && (
        <div className="bg-white rounded-lg border p-6" data-testid="optin-status">
          <h3 className="font-bold text-sm mb-4">SMS Opt-In Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total w/ Phone', value: optInStats.total_with_phone, color: 'bg-blue-500' },
              { label: 'Verified (Can Text)', value: optInStats.verified, color: 'bg-green-500' },
              { label: 'Pending (Waiting)', value: optInStats.pending, color: 'bg-yellow-500' },
              { label: 'Opted Out', value: optInStats.opted_out, color: 'bg-red-500' },
              { label: 'Not Contacted', value: optInStats.uncontacted, color: 'bg-gray-400' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className={`${s.color} text-white text-xl font-bold rounded-lg py-3 mb-1`}>{s.value || 0}</div>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4">Only "Verified" contacts can receive bulk marketing messages. Others need to reply YES first.</p>
        </div>
      )}

      {/* Archives Tab */}
      {tab === 'archives' && (
        <div data-testid="archives-list">
          {archives.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500 border">
              <p className="text-sm">No archived conversations.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {archives.map((a, idx) => (
                <div key={idx} className="bg-white rounded-lg border p-4 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-sm">{a.contact_name}</span>
                    <span className="text-xs text-gray-400 ml-2">{a.contact_phone}</span>
                    <span className="text-xs text-gray-400 ml-2">{a.message_count} messages</span>
                  </div>
                  <button onClick={() => restoreArchive(a.archive_id)} className="text-[#3a87fe] text-xs hover:underline">Restore</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Credit History Tab */}
      {tab === 'history' && (
        <div data-testid="credit-history">
          {history.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500 border">
              <p className="text-sm">No recharge history yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Amount</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Balance After</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Note</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {history.map((h, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 text-xs">{h.recharged_at ? new Date(h.recharged_at).toLocaleString() : '-'}</td>
                      <td className="px-4 py-3 text-xs font-semibold text-green-600">+${h.amount?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs">${h.balance_after?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{h.note || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{h.recharged_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SMSDashboard;
